import React, { useState, useRef } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import Map, { Marker, Source, Layer } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import SaveIcon from '@mui/icons-material/Save'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'

import { vendorSelfDeliveryZones, saveVendorSelfDeliveryZones, getRestaurantProfile } from '../apollo'
import { transformPolygon, transformPath } from '../utils/coordinates'
import ConfigurableValues from '../config/constants'
import { ZONE_COLORS } from '../components/DeliveryBoundaries/ZoneEditor'
import DistanceBandEditor from '../components/DeliveryBoundaries/DistanceBandEditor'
import DrawControl from '../components/MapDraw/DrawControl'
import { drivingAreaPath } from '../utils/drivingArea'
import DrivingDistanceField from '../components/DeliveryBoundaries/DrivingDistanceField'
import { deliveryZonesTheme, DZ, focusRing } from './deliveryZonesTheme'

const GET_SELF_ZONES = gql`${vendorSelfDeliveryZones}`
const SAVE_SELF_ZONES = gql`${saveVendorSelfDeliveryZones}`
const GET_PROFILE = gql`${getRestaurantProfile}`

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' }

const DRAW_STYLES = [
  { id: 'gl-draw-polygon-fill', type: 'fill', filter: ['all', ['==', '$type', 'Polygon']], paint: { 'fill-color': '#6366f1', 'fill-opacity': 0.25 } },
  { id: 'gl-draw-polygon-stroke', type: 'line', filter: ['all', ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#4f46e5', 'line-width': 3 } },
  { id: 'gl-draw-line', type: 'line', filter: ['all', ['==', '$type', 'LineString']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#4f46e5', 'line-width': 3 } },
  { id: 'gl-draw-vertex-halo', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']], paint: { 'circle-radius': 7, 'circle-color': '#fff' } },
  { id: 'gl-draw-vertex', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']], paint: { 'circle-radius': 5, 'circle-color': '#4f46e5' } },
]

function polygonToPath(polygon) {
  if (!polygon?.coordinates?.[0]) return []
  return transformPolygon(polygon.coordinates[0])
}
function pathToPolygonInput(path) {
  return { coordinates: transformPath(path) }
}
function pathToGeoJsonFeature(path) {
  if (!path || path.length < 3) return null
  const ring = path.map(p => [p.lng, p.lat])
  ring.push([path[0].lng, path[0].lat])
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }
}
function drawFeatureToPath(feature) {
  const ring = feature?.geometry?.coordinates?.[0]
  if (!ring || ring.length < 4) return []
  return ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }))
}
// ---- Presentation ----
const PAGE = {
  bgcolor: DZ.page, color: DZ.text, fontFamily: DZ.font, fontSize: 15, lineHeight: 1.45,
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }
}
const CONTENT = {
  maxWidth: 1100, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 }
}
const CARD = { bgcolor: DZ.card, border: `1px solid ${DZ.border}`, borderRadius: '16px', boxShadow: 'none', overflow: 'hidden' }
const CARD_TITLE = { fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: DZ.text }
const BODY = { fontSize: 15, lineHeight: 1.5, color: DZ.body, '& strong': { fontWeight: 700, color: DZ.text } }
const TILE = { display: 'grid', placeItems: 'center', flexShrink: 0 }
// Segmented control. The grey track sits on the flex container, not the Tabs
// root, so the scrollable Tabs can still measure its own overflow.
const TABS = {
  minHeight: 0,
  '& .MuiTabs-flexContainer': { display: 'inline-flex', gap: '4px', p: '4px', bgcolor: DZ.segmentTrack, borderRadius: '12px' },
  '& .MuiTabs-indicator': { display: 'none' },
  '& .MuiTab-root': {
    minHeight: 44, height: 44, minWidth: 0, px: { xs: '14px', sm: '18px' }, py: 0, borderRadius: '9px',
    textTransform: 'none', fontSize: { xs: 14, sm: 15 }, fontWeight: 600, letterSpacing: 0, color: DZ.icon, opacity: 1,
    transition: 'background-color 150ms ease, color 150ms ease',
    '& .MuiTab-iconWrapper': { mr: 1, mb: 0 },
    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.06)' },
    '&.Mui-focusVisible': focusRing
  },
  '& .MuiTab-root.Mui-selected': { bgcolor: DZ.text, color: '#FFFFFF', fontWeight: 700 },
  '& .MuiTab-root.Mui-selected:hover': { bgcolor: DZ.text }
}
const GHOST_BTN = {
  width: 44, height: 44, borderRadius: '10px', flexShrink: 0, color: DZ.secondary, bgcolor: 'transparent',
  '& .MuiSvgIcon-root': { fontSize: 20 }
}
const DASHED_BTN = {
  height: 44, borderRadius: '10px', px: 2, bgcolor: DZ.card, color: DZ.text, fontWeight: 700,
  border: `1px dashed ${DZ.dashed}`,
  '&:hover': { bgcolor: DZ.page, border: `1px dashed ${DZ.text}` }
}
// The selected area's name: no box, just an underline.
const AREA_NAME_INPUT = {
  flex: 1, minWidth: 0,
  '& .MuiInputBase-root': { fontSize: 15, color: DZ.text, borderRadius: '2px' },
  '& .MuiInputBase-root.Mui-focused': focusRing,
  '& input': { fontWeight: 700, py: '4px' },
  '& .MuiInput-root:before': { borderBottom: `1.5px solid ${DZ.underline}` },
  '& .MuiInput-root:hover:not(.Mui-disabled):before': { borderBottom: `1.5px solid ${DZ.secondary}` },
  '& .MuiInput-root:after': { borderBottom: `1.5px solid ${DZ.text}` }
}

export default function VendorDeliveryZones() {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const restaurantId = localStorage.getItem('restaurantId')

  const [tab, setTab] = useState(0) // 0 = fee, 1 = boundary
  const [zones, setZones] = useState([])
  const [selectedZoneIndex, setSelectedZoneIndex] = useState(null)
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.006 })
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [editingZoneIndex, setEditingZoneIndex] = useState(null)
  const [modalMethod, setModalMethod] = useState('draw') // 'draw' | 'distance'
  // Held as typed, so a half-finished "2." doesn't snap back mid-keystroke.
  const [modalDistanceMiles, setModalDistanceMiles] = useState('5')
  const [modalError, setModalError] = useState('')
  // Building a driving area is a Mapbox round trip, so the button waits on it.
  const [modalBusy, setModalBusy] = useState(false)

  const mapRef = useRef(null)
  const drawRef = useRef(null)
  const [drawActive, setDrawActive] = useState(false)

  const { data: profileData } = useQuery(GET_PROFILE, {
    variables: { id: restaurantId },
    skip: !restaurantId,
    onCompleted: (data) => {
      const coords = data?.restaurant?.location?.coordinates
      if (coords && coords.length >= 2) setMapCenter({ lat: parseFloat(coords[1]), lng: parseFloat(coords[0]) })
    },
  })
  const isSelfDeliveryEnabled = profileData?.restaurant?.selfDelivery === true
  const restaurantName = profileData?.restaurant?.name || 'Restaurant'

  const { loading: zonesLoading, refetch: refetchZones } = useQuery(GET_SELF_ZONES, {
    variables: { restaurantId },
    skip: !restaurantId || !isSelfDeliveryEnabled,
    onCompleted: (data) => {
      const loaded = (data?.vendorSelfDeliveryZones || []).map(z => ({
        _id: z._id, name: z.name, sortOrder: z.sortOrder ?? 0, polygon: z.polygon, _path: polygonToPath(z.polygon),
      }))
      setZones(loaded)
      setSelectedZoneIndex(loaded.length > 0 ? 0 : null)
    },
  })

  const [mutSaveZones, { loading: saving }] = useMutation(SAVE_SELF_ZONES, {
    onCompleted: () => {
      setSuccessMsg('Delivery boundary saved.')
      setErrorMsg('')
      refetchZones()
      setTimeout(() => setSuccessMsg(''), 4000)
    },
    onError: (err) => { setErrorMsg(err.message || 'Failed to save'); setSuccessMsg('') },
  })

  const nextAreaName = (len) => `Area ${len + 1}`

  function commitZonePath(path) {
    if (modalMode === 'edit' && editingZoneIndex !== null) {
      setZones(prev => prev.map((z, i) => i === editingZoneIndex ? { ...z, _path: path, polygon: pathToPolygonInput(path) } : z))
      setSelectedZoneIndex(editingZoneIndex)
    } else {
      const newIndex = zones.length
      setZones(prev => [...prev, { name: nextAreaName(prev.length), sortOrder: prev.length, _path: path, polygon: pathToPolygonInput(path) }])
      setSelectedZoneIndex(newIndex)
    }
    setModalOpen(false)
  }

  function handleDrawReady(draw) {
    drawRef.current = draw
    if (!draw) return
    if ((draw.getAll()?.features?.length || 0) > 0) return
    const existing = editingZoneIndex !== null ? zones[editingZoneIndex]?._path : null
    if (existing && existing.length >= 3) {
      const feature = pathToGeoJsonFeature(existing)
      const [id] = draw.add(feature)
      draw.changeMode('direct_select', { featureId: id })
    } else {
      draw.changeMode('draw_polygon')
    }
  }

  function syncDrawToZone() {
    const draw = drawRef.current
    if (!draw) return
    const feature = draw.getAll()?.features?.[0]
    if (!feature) return
    const path = drawFeatureToPath(feature)
    if (path.length < 3) return
    setZones(prev => prev.map((z, i) => i === editingZoneIndex ? { ...z, _path: path, polygon: pathToPolygonInput(path) } : z))
  }

  function handleDrawCreate() {
    const draw = drawRef.current
    if (!draw) return
    const feature = draw.getAll()?.features?.[0]
    if (!feature) return
    const path = drawFeatureToPath(feature)
    if (path.length < 3) return
    if (editingZoneIndex !== null) {
      syncDrawToZone()
    } else {
      const newIndex = zones.length
      setZones(prev => [...prev, { name: nextAreaName(prev.length), sortOrder: prev.length, _path: path, polygon: pathToPolygonInput(path) }])
      setSelectedZoneIndex(newIndex)
      setEditingZoneIndex(newIndex)
    }
    if (feature.id != null) draw.changeMode('direct_select', { featureId: feature.id })
  }

  function finishDrawing() {
    const draw = drawRef.current
    const feature = draw?.getAll()?.features?.[0]
    const drawnPath = feature ? drawFeatureToPath(feature) : null
    setZones(prev => {
      let next = prev
      if (drawnPath && drawnPath.length >= 3) {
        if (editingZoneIndex !== null && prev[editingZoneIndex]) {
          next = prev.map((z, i) => i === editingZoneIndex ? { ...z, _path: drawnPath, polygon: pathToPolygonInput(drawnPath) } : z)
        } else {
          next = [...prev, { name: nextAreaName(prev.length), sortOrder: prev.length, _path: drawnPath, polygon: pathToPolygonInput(drawnPath) }]
        }
      }
      persistZones(next)
      return next
    })
    setDrawActive(false)
    setEditingZoneIndex(null)
  }

  function persistZones(zoneList) {
    if (!restaurantId) return
    const zonesInput = (zoneList || [])
      .filter(z => z._path && z._path.length >= 3)
      .map((z, idx) => ({ name: z.name?.trim() || nextAreaName(idx), fee: 0, polygon: pathToPolygonInput(z._path), sortOrder: idx }))
    mutSaveZones({ variables: { restaurantId, zones: zonesInput } })
  }

  function openAddModal() {
    setDrawActive(false)
    setModalMode('add')
    setEditingZoneIndex(null)
    setModalMethod('draw')
    setModalDistanceMiles('5')
    setModalError('')
    setModalOpen(true)
  }

  function openEditModal(index) {
    setDrawActive(false)
    setModalMode('edit')
    setEditingZoneIndex(index)
    setModalMethod('draw')
    setModalDistanceMiles('5')
    setModalError('')
    setSelectedZoneIndex(index)
    setModalOpen(true)
  }

  async function handleModalContinue() {
    if (modalMethod === 'draw') {
      setModalOpen(false)
      setDrawActive(true)
      return
    }
    // Driving distance: what a driver can actually reach by road, from Mapbox.
    // Shown as an error on failure rather than quietly falling back to a
    // circle, which is the straight-line shape this replaced.
    setModalBusy(true)
    setModalError('')
    try {
      const path = await drivingAreaPath(mapCenter, modalDistanceMiles, MAPBOX_TOKEN)
      commitZonePath(path)
    } catch (e) {
      setModalError(e.message)
    } finally {
      setModalBusy(false)
    }
  }

  function handleDeleteZone(index) {
    setZones(prev => prev.filter((_, i) => i !== index))
    setSelectedZoneIndex(prev => (prev === null || prev === index) ? null : (prev > index ? prev - 1 : prev))
  }

  function handleNameChange(index, value) {
    setZones(prev => prev.map((z, i) => i === index ? { ...z, name: value } : z))
  }

  function handleSave() {
    if (zones.some(z => !z._path || z._path.length < 3)) { setErrorMsg('A boundary area has fewer than 3 points.'); return }
    setErrorMsg('')
    persistZones(zones)
  }

  if (!isSelfDeliveryEnabled && profileData) {
    return (
      <ThemeProvider theme={deliveryZonesTheme}>
        <Box sx={PAGE}>
          <Box sx={CONTENT}>
            <Paper elevation={0} sx={{ ...CARD, p: { xs: 3, sm: 5 }, textAlign: 'center', maxWidth: 560, mx: 'auto' }}>
              <Box aria-hidden sx={{ ...TILE, width: 52, height: 52, borderRadius: '14px', mx: 'auto', mb: 2, bgcolor: DZ.tileBg, color: DZ.secondary }}>
                <LockOutlinedIcon sx={{ fontSize: 26 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.3, color: DZ.text, mb: 1 }}>Self Delivery Not Enabled</Typography>
              <Typography sx={{ ...BODY }}>
                Enable <strong>Self Delivery</strong> in your Profile settings under Order Modes to manage your delivery.
              </Typography>
            </Paper>
          </Box>
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={deliveryZonesTheme}>
      <Box sx={PAGE}>
        <Box sx={{ ...CONTENT, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header */}
          <Box component="header">
            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: DZ.text }}>Delivery</Typography>
            <Typography sx={{ fontSize: 15, color: DZ.secondary, mt: 0.75 }}>
              Set your delivery fee and the area you deliver to
            </Typography>
          </Box>

          {/* Tabs: a segmented control sized to its content */}
          <Box>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)}
              variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
              sx={TABS}>
              <Tab icon={<RouteOutlinedIcon fontSize="small" />} iconPosition="start" label="Delivery fee" />
              <Tab icon={<MapOutlinedIcon fontSize="small" />} iconPosition="start" label="Delivery boundary" />
            </Tabs>
          </Box>

          {/* TAB 0: Delivery fee (own distance tiers) */}
          {tab === 0 && (
            <Paper elevation={0} sx={CARD}>
              <DistanceBandEditor restaurantId={restaurantId} onSaved={refetchZones} />
            </Paper>
          )}

          {/* TAB 1: Delivery boundary (own area) */}
          {tab === 1 && (
            <Paper elevation={0} sx={{ ...CARD, p: { xs: 2.5, sm: '28px 32px 32px' }, display: 'flex', flexDirection: 'column', gap: 3, containerType: 'inline-size' }}>
              <Box>
                <Typography component="h2" sx={CARD_TITLE}>Delivery boundary</Typography>
                <Typography sx={{ ...BODY, mt: 0.75, maxWidth: 760 }}>
                  Set the maximum area you deliver to. Customers outside it can’t order — so you must
                  have a boundary to take delivery orders.
                </Typography>
              </Box>
              {/* Area list beside the map once the card is wide enough; stacked, list first, below that. */}
              <Box sx={{
                display: 'flex', flexDirection: 'column', gap: 3,
                '@container (min-width: 760px)': { flexDirection: 'row', height: 600 }
              }}>
                {/* Boundary list */}
                <Box sx={{
                  display: 'flex', flexDirection: 'column', minWidth: 0,
                  '@container (min-width: 760px)': { width: 320, flexShrink: 0 },
                  '@container (min-width: 900px)': { width: 344 }
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: DZ.text }}>Boundary areas ({zones.length})</Typography>
                    <Button size="small" startIcon={<AddRoundedIcon />} onClick={openAddModal}
                      variant="outlined" sx={DASHED_BTN}>Add area</Button>
                  </Box>
                  {zonesLoading && <CircularProgress size={22} sx={{ alignSelf: 'center', my: 2 }} />}
                  <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, p: '2px', m: '-2px' }}>
                    {zones.length === 0 && !zonesLoading && (
                      <Typography variant="body2" sx={{ fontSize: 14, color: DZ.secondary, p: '16px 18px', borderRadius: '12px', border: `1px dashed ${DZ.inputBorder}` }}>
                        No boundary yet. Add an area (draw freehand, or a distance radius) so you can take
                        delivery orders.
                      </Typography>
                    )}
                    {zones.map((zone, i) => {
                      const color = ZONE_COLORS[i % ZONE_COLORS.length]
                      const isSel = selectedZoneIndex === i
                      return (
                        <Box key={i} onClick={() => setSelectedZoneIndex(i)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, minHeight: 56, pl: '14px', pr: '6px', flexShrink: 0,
                            borderRadius: '12px', cursor: 'pointer', boxSizing: 'border-box',
                            // 1px border plus a 1px inset ring reads as 2px when selected, without shifting the row.
                            border: `1px solid ${isSel ? color : DZ.border}`,
                            boxShadow: isSel ? `inset 0 0 0 1px ${color}` : 'none',
                            backgroundColor: isSel ? `${color}14` : DZ.card,
                            transition: 'border-color 150ms ease, background-color 150ms ease',
                            '&:hover': { borderColor: isSel ? color : '#C4CAD4' }
                          }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                          {isSel ? (
                            <TextField value={zone.name} onChange={e => handleNameChange(i, e.target.value)}
                              onClick={e => e.stopPropagation()} size="small" variant="standard" placeholder="Area name"
                              sx={AREA_NAME_INPUT} />
                          ) : (
                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: DZ.text, overflowWrap: 'anywhere' }}>{zone.name}</Typography>
                          )}
                          <Tooltip title="Redraw / edit shape">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); openEditModal(i) }} sx={{ ...GHOST_BTN, '&:hover': { bgcolor: DZ.ghostHover, color: DZ.text } }}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete area">
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteZone(i) }} sx={{ ...GHOST_BTN, '&:hover': { bgcolor: DZ.ghostHover, color: DZ.danger } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    })}
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {successMsg && <Alert severity="success">{successMsg}</Alert>}
                    {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
                    <Button variant="contained" fullWidth onClick={handleSave} disabled={saving || zones.length === 0}
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      sx={{ height: 48, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
                      {saving ? 'Saving…' : 'Save boundary'}
                    </Button>
                  </Box>
                </Box>

                {/* Map */}
                {/* Stacked: a fixed 420px below the list. Side by side: fills the row's 600px. */}
                <Box sx={{
                  flex: 'none', minWidth: 0, height: 420, position: 'relative',
                  borderRadius: '14px', overflow: 'hidden', border: `1px solid ${DZ.frame}`,
                  '@container (min-width: 760px)': { flex: 1, height: 'auto' }
                }}>
                  {drawActive && (
                    <Box sx={{
                      position: 'absolute', top: 12, right: 12, zIndex: 2, maxWidth: 'calc(100% - 24px)', boxSizing: 'border-box',
                      display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center',
                      bgcolor: DZ.card, border: `1px solid ${DZ.border}`, borderRadius: '12px', pl: 1.75, pr: 0.75, py: 0.75,
                      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)'
                    }}>
                      <Typography variant="caption" sx={{ fontSize: 13, color: DZ.secondary }}>Click &amp; drag to draw · release to close</Typography>
                      <Button size="small" variant="contained" onClick={finishDrawing} sx={{ borderRadius: '10px', textTransform: 'none' }}>Done drawing</Button>
                    </Box>
                  )}
                  <Map
                    ref={mapRef}
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{ longitude: mapCenter.lng, latitude: mapCenter.lat, zoom: 11 }}
                    style={MAP_CONTAINER_STYLE}
                    mapStyle="mapbox://styles/mapbox/streets-v12"
                  >
                    {drawActive && (
                      <DrawControl position="top-left" freehand styles={DRAW_STYLES} displayControlsDefault={false}
                        controls={{ polygon: true, trash: true }} defaultMode="simple_select"
                        onReady={handleDrawReady} onCreate={handleDrawCreate} onUpdate={syncDrawToZone} onDelete={syncDrawToZone} />
                    )}
                    <Marker longitude={mapCenter.lng} latitude={mapCenter.lat} anchor="center">
                      <div title={restaurantName} style={{ width: 18, height: 18, borderRadius: '50%', background: '#D32F2F', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
                    </Marker>
                    {zones.map((zone, i) => {
                      if (!zone._path || zone._path.length < 3) return null
                      if (drawActive && i === editingZoneIndex) return null
                      const color = ZONE_COLORS[i % ZONE_COLORS.length]
                      const isSel = selectedZoneIndex === i
                      const feature = pathToGeoJsonFeature(zone._path)
                      if (!feature) return null
                      return (
                        <Source key={i} id={`vzone-${i}`} type="geojson" data={feature}>
                          <Layer id={`vzone-fill-${i}`} type="fill" paint={{ 'fill-color': color, 'fill-opacity': isSel ? 0.3 : 0.15 }} />
                          <Layer id={`vzone-line-${i}`} type="line" paint={{ 'line-color': color, 'line-width': isSel ? 3 : 1.5 }} />
                        </Source>
                      )
                    })}
                  </Map>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Add / Edit boundary-area modal */}
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth
          sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(15, 23, 42, 0.62)' } }}
          PaperProps={{
            sx: {
              borderRadius: '18px', boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
              // The dialog renders outside the page box, so it sets its own surface and font.
              bgcolor: '#FFFFFF', backgroundImage: 'none', color: DZ.text, fontFamily: DZ.font,
              m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' }
            }
          }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.25, color: DZ.text, p: { xs: '22px 20px 8px', sm: '26px 28px 8px' } }}>{modalMode === 'edit' ? 'Edit boundary area' : 'Add boundary area'}</DialogTitle>
          <DialogContent sx={{ px: { xs: '20px', sm: '28px' }, pb: 3 }}>
            {modalError && <Alert severity="error" sx={{ mb: 2 }}>{modalError}</Alert>}
            <Typography variant="body2" sx={{ fontSize: 15, color: DZ.body }} mb={1}>How do you want to define the area?</Typography>
            <RadioGroup value={modalMethod} onChange={e => { setModalMethod(e.target.value); setModalError('') }}>
              <FormControlLabel value="draw" control={<Radio />} label="Draw on map (freehand)" />
              <FormControlLabel value="distance" control={<Radio />} label="Driving distance from restaurant" />
            </RadioGroup>
            {modalMethod === 'draw' && (
              <Typography variant="caption" sx={{ display: 'block', ml: 4, mt: 0.5, fontSize: 13, lineHeight: 1.5, color: DZ.secondary, '& strong': { color: DZ.text } }}>
                The map switches to draw mode — click &amp; drag to trace the area, then press <strong>Done drawing</strong>.
              </Typography>
            )}
            {modalMethod === 'distance' && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <DrivingDistanceField
                  value={modalDistanceMiles}
                  onChange={v => { setModalDistanceMiles(v); setModalError('') }}
                  disabled={modalBusy}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{
            px: { xs: '20px', sm: '28px' }, py: '16px', gap: '10px', flexWrap: 'wrap',
            borderTop: `1px solid ${DZ.border}`,
            '& > :not(style) ~ :not(style)': { ml: 0 }
          }}>
            <Button onClick={() => setModalOpen(false)} disabled={modalBusy} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={handleModalContinue}
              variant="contained"
              disabled={modalBusy}
              startIcon={modalBusy ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, minWidth: 110 }}>
              {modalMethod === 'draw' ? 'Start drawing' : modalBusy ? 'Measuring roads…' : 'Add area'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  )
}
